import React, { useState, useRef, useMemo, useCallback } from 'react';
import EmojiPicker, { EmojiStyle, EmojiClickData, SuggestionMode } from 'emoji-picker-react';
import { ChevronLeft, Search, Image, RefreshCw } from 'lucide-react';
import { icons } from 'lucide-react';
import { PhoneHQButton, PhoneHQTabs } from 'ch3-ui-lib';

interface AvatarPickerProps {
    onSave: (file: File) => void;
    onCancel: () => void;
}

type TabType = 'emoji' | 'icon' | 'upload';
const TAB_IDS: TabType[] = ['emoji', 'icon', 'upload'];

const CURATED_ICONS = [
    'Asterisk', 'AtSign', 'Briefcase', 'Building', 'Bus', 'Camera', 'Car', 'Clock', 'Cloud',
    'Code', 'Coffee', 'Compass', 'Crown', 'Database', 'Diamond', 'Drum', 'Dumbbell',
    'Flag', 'Flame', 'FlaskConical', 'Flower', 'Folder', 'Gamepad', 'Gift', 'Globe',
    'GraduationCap', 'Hammer', 'Headphones', 'Heart', 'Home', 'Image', 'Key', 'Lamp',
    'Laptop', 'Leaf', 'Library', 'Lightbulb', 'Link', 'Lock', 'Map', 'MapPin', 'Megaphone',
    'MessageSquare', 'Mic', 'Monitor', 'Moon', 'Mountain', 'Music', 'Paintbrush',
    'Palette', 'PenTool', 'Phone', 'Plane', 'Puzzle', 'Rocket', 'School', 'Search',
    'Settings', 'Shield', 'Ship', 'ShoppingCart', 'Smile', 'Snowflake', 'Star',
    'Sun', 'Swords', 'Target', 'Terminal', 'TreePine', 'Trophy', 'Truck', 'Tv',
    'Umbrella', 'Utensils', 'Video', 'Wallet', 'Watch', 'Wifi', 'Wrench', 'Zap'
] as const;

const PRESET_COLORS = [
    '#2D2D2D', '#9CA3AF', '#92400E', '#D97706', '#EA580C',
    '#059669', '#6366f1', '#D946EF', '#E11D48', '#DC2626',
] as const;

const DEFAULT_ICON_COLOR = PRESET_COLORS[6];

function canvasToFile(canvas: HTMLCanvasElement): Promise<File> {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(new File([blob!], 'avatar.png', { type: 'image/png' }));
        }, 'image/png');
    });
}

function drawRoundedRectBackground(ctx: CanvasRenderingContext2D, size: number, radius: number, color: string) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(0, 0, size, size, radius);
    ctx.fill();
}

async function renderEmojiToFile(emoji: string): Promise<File> {
    const size = 256;
    const radius = 32;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    drawRoundedRectBackground(ctx, size, radius, '#f4f4f5');

    ctx.font = '148px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emoji, size / 2, size / 2 + 8);

    return canvasToFile(canvas);
}

async function renderIconToFile(iconName: string, iconColor: string): Promise<File> {
    const size = 256;
    const radius = 32;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    drawRoundedRectBackground(ctx, size, radius, '#f4f4f5');

    const IconComp = icons[iconName as keyof typeof icons];
    if (IconComp) {
        const hiddenEl = document.createElement('div');
        hiddenEl.style.cssText = 'position:absolute;left:-9999px;top:-9999px';
        document.body.appendChild(hiddenEl);

        const { createRoot } = await import('react-dom/client');
        const root = createRoot(hiddenEl);

        await new Promise<void>((resolve) => {
            root.render(
                React.createElement(IconComp, { size: 128, color: iconColor, strokeWidth: 2 })
            );
            requestAnimationFrame(() => resolve());
        });

        const renderedSvg = hiddenEl.querySelector('svg');
        if (renderedSvg) {
            renderedSvg.setAttribute('width', '128');
            renderedSvg.setAttribute('height', '128');
            const svgString = new XMLSerializer().serializeToString(renderedSvg);

            const img = new window.Image();
            const blob = new Blob([svgString], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);

            await new Promise<void>((resolve) => {
                img.onload = () => {
                    ctx.drawImage(img, (size - 128) / 2, (size - 128) / 2, 128, 128);
                    URL.revokeObjectURL(url);
                    resolve();
                };
                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    resolve();
                };
                img.src = url;
            });
        }

        root.unmount();
        document.body.removeChild(hiddenEl);
    }

    return canvasToFile(canvas);
}

const AvatarPicker: React.FC<AvatarPickerProps> = ({ onSave, onCancel }) => {
    const [activeTab, setActiveTab] = useState<TabType>('emoji');
    const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);
    const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
    const [iconColor, setIconColor] = useState<string>(DEFAULT_ICON_COLOR);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadPreview, setUploadPreview] = useState<string | null>(null);
    const [iconSearchTerm, setIconSearchTerm] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [colorPickerOpen, setColorPickerOpen] = useState(false);


    const filteredIcons = useMemo(() => {
        const term = iconSearchTerm.toLowerCase();
        if (!term) return CURATED_ICONS as unknown as string[];
        return (CURATED_ICONS as unknown as string[]).filter(name =>
            name.toLowerCase().includes(term)
        );
    }, [iconSearchTerm]);

    const randomizeIcon = useCallback(() => {
        const randomIndex = Math.floor(Math.random() * CURATED_ICONS.length);
        setSelectedIcon(CURATED_ICONS[randomIndex]);
    }, []);

    const handleEmojiClick = useCallback(async (emojiData: EmojiClickData) => {
        setSelectedEmoji(emojiData.emoji);
        setSelectedIcon(null);
        setUploadedFile(null);
        setUploadPreview(null);
        const file = await renderEmojiToFile(emojiData.emoji);
        onSave(file);
    }, [onSave]);

    const handleIconClick = useCallback(async (iconName: string) => {
        setSelectedIcon(iconName);
        setSelectedEmoji(null);
        setUploadedFile(null);
        setUploadPreview(null);
        const file = await renderIconToFile(iconName, iconColor);
        onSave(file);
    }, [iconColor, onSave]);

    const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            alert('File size must be under 10 MB');
            return;
        }

        setUploadedFile(file);
        setUploadPreview(URL.createObjectURL(file));
        setSelectedEmoji(null);
        setSelectedIcon(null);
    }, []);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            if (activeTab === 'upload' && uploadedFile) {
                onSave(uploadedFile);
            } else if (activeTab === 'emoji' && selectedEmoji) {
                const file = await renderEmojiToFile(selectedEmoji);
                onSave(file);
            } else if (activeTab === 'icon' && selectedIcon) {
                const file = await renderIconToFile(selectedIcon, iconColor);
                onSave(file);
            }
        } finally {
            setIsSaving(false);
        }
    }, [activeTab, uploadedFile, selectedEmoji, selectedIcon, iconColor, onSave]);

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
                <button
                    onClick={onCancel}
                    className="text-ink-muted hover:text-ink-secondary transition-colors p-1"
                >
                    <ChevronLeft className="p-1.5" size={30 } />
                </button>
                <h3 className="text-base font-semibold text-ink">Change avatar</h3>
            </div>

            {/* Tabs + Content */}
            <PhoneHQTabs
                activeTab={activeTab}
                onTabChange={(id: string) => setActiveTab(id as TabType)}
                contentClassName="p-4"
                tabs={TAB_IDS.map((id) => ({
                    id,
                    label: id,
                    content: id === 'emoji' ? (
                        <div className="flex flex-col gap-3">

                            <EmojiPicker
                                onEmojiClick={handleEmojiClick}
                                emojiStyle={
                                EmojiStyle.TWITTER}
                                suggestedEmojisMode={SuggestionMode.RECENT}
                                width="100%"
                                height={420}
                                searchPlaceholder="Search emoji..."
                                lazyLoadEmojis
                            />
                        </div>
                    ) : id === 'icon' ? (
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Search icons..."
                                    value={iconSearchTerm}
                                    onChange={(e) => setIconSearchTerm(e.target.value)}
                                    className="flex-1 px-3 py-1.5 text-sm border border-fill-subtle rounded-md focus:outline-none focus:ring-2 focus:ring-brand-focus focus:border-brand"
                                />
                                <button
                                    onClick={randomizeIcon}
                                    className="p-1.5 text-ink-muted hover:text-ink-secondary border border-fill-subtle rounded-md hover:bg-canvas transition-colors"
                                    title="Random icon"
                                >
                                    <RefreshCw className="p-1.5" size={32} />
                                </button>
                                <div className="relative">
                                    <button
                                        onClick={() => setColorPickerOpen(!colorPickerOpen)}
                                        className="w-8 h-8 rounded-lg border border-fill-subtle cursor-pointer flex-shrink-0"
                                        style={{ backgroundColor: iconColor }}
                                        title="Icon color"
                                    />
                                    {colorPickerOpen && (
                                        <>
                                            <div
                                                className="fixed inset-0 z-40"
                                                onClick={() => setColorPickerOpen(false)}
                                            />
                                            <div className="absolute right-0 top-10 z-50 bg-surface rounded-xl shadow-lg border border-line p-3 w-max">
                                                <div className="grid grid-cols-5 gap-2">
                                                    {PRESET_COLORS.map((color) => (
                                                        <button
                                                            key={color}
                                                            onClick={() => {
                                                                setIconColor(color);
                                                                setColorPickerOpen(false);
                                                            }}
                                                            className={`w-9 h-9 rounded-lg transition-all ${
                                                                iconColor === color
                                                                    ? 'ring-2 ring-offset-2 ring-brand-focus'
                                                                    : 'hover:scale-110'
                                                            }`}
                                                            style={{ backgroundColor: color }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-8 gap-1.5 overflow-y-auto max-h-80">
                                {filteredIcons.map((iconName) => {
                                    const IconComp = icons[iconName as keyof typeof icons];
                                    if (!IconComp) return null;
                                    return (
                                        <button
                                            key={iconName}
                                            onClick={() => handleIconClick(iconName)}
                                            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                                                selectedIcon === iconName
                                                    ? 'ring-2 ring-brand-focus'
                                                    : 'hover:bg-surface-muted'
                                            }`}
                                            title={iconName}
                                        >
                                            <IconComp size={32} color={iconColor} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-4 py-6">
                            {uploadPreview ? (
                                <>
                                    <div className={'flex justify-center text-ink-muted text-tiny'}>
                                        Preview
                                    </div>
                                    <div className="flex items-end gap-3">
                                        <div className="size-32 rounded-3xl overflow-hidden">
                                            <img src={uploadPreview} alt="128px" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="size-10 rounded-xl overflow-hidden">
                                            <img src={uploadPreview} alt="40px" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="size-5 rounded-md overflow-hidden">
                                            <img src={uploadPreview} alt="20px" className="w-full h-full object-cover" />
                                        </div>
                                    </div>
                                    <PhoneHQButton
                                        variant="secondary"
                                        size="sm"
                                        className={'px-4 box-border shadow-md w-[242px]'}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <Search size={18} />
                                        Change image
                                    </PhoneHQButton>
                                </>
                            ) : (
                                <>
                                    <div className="size-16 rounded-2xl bg-surface-muted flex items-center justify-center">
                                        <Image size={24} className="text-ink-muted" />
                                    </div>
                                    <PhoneHQButton
                                        className={'px-4 box-border w-[242px] shadow-md'}
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <Search className={'!text-ink-muted'} size={18}/>
                                        Upload an image
                                    </PhoneHQButton>
                                    <p className="text-xs text-ink-muted">
                                        Any JPG, JPEG, PNG or GIF up to 10 MB
                                    </p>
                                </>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/gif"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                        </div>
                    ),
                }))}
            />

            {/* Footer - only for upload tab */}
            {activeTab === 'upload' && (
                <div className="flex justify-between px-4 py-3 border-t border-line">
                    <PhoneHQButton variant="tertiary" onClick={onCancel}>
                        Cancel
                    </PhoneHQButton>
                    <PhoneHQButton
                        onClick={handleSave}
                        disabled={!uploadedFile || isSaving}
                    >
                        Save
                    </PhoneHQButton>
                </div>
            )}
        </div>
    );
};

export default AvatarPicker;
